import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldAlert, ShieldCheck } from "lucide-react";

export default function PublicReceipt() {
  const { hash } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/receipts/${hash}`);
        setData(r.data);
      } catch { setErr(true); }
    })();
  }, [hash]);

  if (err) {
    return (
      <div className="text-center py-20" data-testid="receipt-not-found">
        <p className="eyebrow mb-3">Receipt not found</p>
        <h1 className="font-display text-[36px]">No public receipt exists for this reference.</h1>
        <Link to="/" className="bc-btn-text mt-6 inline-flex">Back to overview →</Link>
      </div>
    );
  }
  if (!data) return <div className="text-sm text-[hsl(var(--bc-muted))]">Loading receipt…</div>;

  const isKatlasSigned = data.verification_status === "node_signed" && !!data.katlas_attestation_reference;
  const title = isKatlasSigned ? "KATLAS-signed delivery receipt." : "Governed action recorded — pending KATLAS signing.";

  return (
    <div className="max-w-3xl mx-auto" data-testid="public-receipt">
      <p className="eyebrow eyebrow-signal">Public delivery receipt</p>
      <h1 className="font-display text-[40px] sm:text-[48px] tracking-tight leading-[1.05] mt-2" data-testid="receipt-title">
        {title}
      </h1>
      <p className="mt-4 text-[15px] text-[hsl(var(--bc-ink-soft))]">
        Whitelisted metadata only. Raw artefacts, prompts, source code, logs and secrets remain at source.
      </p>

      {/* Integrity banner — Stage 1 vs Stage 2 cannot be confused */}
      <div
        className={`mt-6 flex items-start gap-3 p-5 rounded border ${isKatlasSigned ? "border-[hsl(var(--bc-success)/0.4)] bg-[hsl(var(--bc-success)/0.06)]" : "border-[hsl(var(--bc-signal)/0.4)] bg-[hsl(var(--bc-signal)/0.06)]"}`}
        data-testid={isKatlasSigned ? "banner-katlas-signed" : "banner-pending-unconfigured"}
      >
        {isKatlasSigned ? (
          <ShieldCheck size={20} className="text-[hsl(var(--bc-success))] mt-0.5" />
        ) : (
          <ShieldAlert size={20} className="text-[hsl(var(--bc-signal))] mt-0.5" />
        )}
        <div>
          <p className="font-medium text-[15px]">
            {isKatlasSigned ? "KATLAS-signed" : "Pending — not KATLAS-signed"}
          </p>
          <p className="text-sm text-[hsl(var(--bc-ink-soft))] mt-1">
            {isKatlasSigned
              ? "Signer reference, node timestamp and attestation reference were returned by the KATLAS node."
              : "KATLAS is unconfigured for this environment. No KATLAS signer, node timestamp or attestation reference exists for this action. The reference below is a BuildCierge local reference, not a KATLAS receipt hash."}
          </p>
        </div>
      </div>

      <div className="rule my-8" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-10">
        <KV k="Receipt type" v={data.receipt_type} />
        <KV k="Outcome" v={data.outcome} />
        <KV k="Actor role" v={data.actor_role} />
        <KV k="Counterparty role" v={data.counterparty_role || "—"} />
        <KV k="Policy id" v={data.policy_id} />
        <KV k="Event id" v={data.event_id} mono />
        <KV k="BuildCierge timestamp (UTC)" v={data.timestamp_utc} mono />
        <KV k="Verification status" v={data.verification_status} />
        <KV k="KATLAS status" v={data.katlas_status} />
        <KV k="Withheld evidence" v={`${data.withheld_evidence_count} items`} />
        {data.node_timestamp && <KV k="KATLAS node timestamp" v={data.node_timestamp} mono />}
        {data.signer_reference && <KV k="KATLAS signer reference" v={data.signer_reference} />}
      </div>

      <div className="rule my-8" />

      <p className="eyebrow mb-2">
        {isKatlasSigned ? "KATLAS attestation reference" : "BuildCierge reference (local — not KATLAS-signed)"}
      </p>
      <div className="bc-mono-block" data-testid="primary-reference">
        {isKatlasSigned ? data.katlas_attestation_reference : data.receipt_hash}
      </div>

      <p className="eyebrow mt-8 mb-2">Artifact hash <span className="text-[hsl(var(--bc-muted))]">(BuildCierge deterministic reference)</span></p>
      <div className="bc-mono-block">{data.artifact_hash}</div>

      {data.evidence_hashes?.length > 0 && (
        <>
          <p className="eyebrow mt-8 mb-2">Evidence hashes <span className="text-[hsl(var(--bc-muted))]">({data.evidence_hashes.length} · BuildCierge deterministic references)</span></p>
          <div className="bc-mono-block">
            {data.evidence_hashes.map((h, i) => <div key={i}>{h}</div>)}
          </div>
        </>
      )}

      <div className="mt-10 bc-card p-7">
        <p className="eyebrow eyebrow-signal mb-3">CAR summary</p>
        <p className="text-sm mb-3"><strong className="font-medium">Custody · </strong>{data.car_summary?.custody}</p>
        <p className="text-sm mb-3"><strong className="font-medium">Authority · </strong>{data.car_summary?.authority}</p>
        <p className="text-sm"><strong className="font-medium">Receipts · </strong>{data.car_summary?.receipts}</p>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-[hsl(var(--bc-muted))]">
        <div>
          <p className="eyebrow mb-2">Assertions</p>
          <ul className="space-y-1">
            {(data.assertions || []).map((a, i) => <li key={i}>· {a}</li>)}
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-2">Exceptions</p>
          {(data.exceptions || []).length === 0
            ? <p>None recorded.</p>
            : <ul className="space-y-1">{data.exceptions.map((a, i) => <li key={i}>· {a}</li>)}</ul>}
        </div>
      </div>

      <div className="mt-12 text-xs text-[hsl(var(--bc-muted))]">
        Synthetic demo receipt. No real client names, repository data, secrets, prompts or production logs are exposed.
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <p className="eyebrow mb-1">{k}</p>
      <p className={mono ? "font-mono text-[12px] break-all" : "text-[15px]"}>{v}</p>
    </div>
  );
}
